// frontend/src/hooks/useInventoryData.ts
// React Query hooks for inventory data with backend → frontend adaptation
// Falls back to mock data when the backend is unreachable (dev/offline mode)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchResources,
  fetchRecipes,
  updateResource,
  createResource,
  adjustResourceQuantity,
  resourceToInventoryItem,
  recipeToInventoryItem,
  inventoryItemToResourceUpdate,
} from '../services/inventoryService';
import { initialItems } from '../components/inventaire/inventaireMockData';
import useLocationStore from '../stores/locationStore';
import { useDemoMode } from './useDemoMode';
import type { InventoryItem } from '../components/inventaire/inventaireTypes';
import type { Resource, Recipe } from '../types/models';

// =============================================================================
// Query Keys
// =============================================================================

export const inventoryKeys = {
  all: ['inventory'] as const,
  resources: (locationId?: string) => [...inventoryKeys.all, 'resources', locationId] as const,
  recipes: (locationId?: string) => [...inventoryKeys.all, 'recipes', locationId] as const,
  items: (locationId?: string) => [...inventoryKeys.all, 'items', locationId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch all inventory items (resources) from the backend, mapped to InventoryItem[].
 * Falls back to mock data on error (e.g. backend unreachable).
 */
export function useInventoryItems() {
  const currentLocation = useLocationStore((state) => state.currentLocation);
  const isDemo = useDemoMode();

  return useQuery({
    queryKey: inventoryKeys.items(currentLocation?.id),
    enabled: !!currentLocation,
    queryFn: async (): Promise<{ items: InventoryItem[]; fromBackend: boolean }> => {
      const [resourcesData, recipesData] = await Promise.all([
        fetchResources(),
        fetchRecipes(),
      ]);

      const resources = resourcesData.resources || [];
      const recipes = recipesData.recipes || [];

      // Map resources to frontend InventoryItem
      const resourceItems = resources.map((r: Resource) => resourceToInventoryItem(r, recipes));

      // Map recipes to frontend InventoryItem (for combined view)
      const recipeItems = recipes.map((r: Recipe) => recipeToInventoryItem(r));

      // Combine: resources + recipes that aren't already represented
      const resourceIds = new Set(resources.map((r: Resource) => r.id));
      const combined = [
        ...resourceItems,
        ...recipeItems.filter(ri => !resourceIds.has(ri.id)),
      ];

      return { items: combined, fromBackend: true };
    },
    // In demo mode, show mock data while loading; otherwise show nothing
    placeholderData: isDemo ? { items: initialItems, fromBackend: false } : undefined,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch only resources (for the stocks tab).
 */
export function useResources() {
  const currentLocation = useLocationStore((state) => state.currentLocation);

  return useQuery({
    queryKey: inventoryKeys.resources(currentLocation?.id),
    enabled: !!currentLocation,
    queryFn: async () => {
      const data = await fetchResources();
      return data.resources || [];
    },
    retry: 1,
  });
}

/**
 * Fetch only recipes (for the recettes/configuration tab).
 */
export function useRecipes() {
  const currentLocation = useLocationStore((state) => state.currentLocation);

  return useQuery({
    queryKey: inventoryKeys.recipes(currentLocation?.id),
    enabled: !!currentLocation,
    queryFn: async () => {
      const data = await fetchRecipes();
      return data.recipes || [];
    },
    retry: 1,
  });
}

/**
 * Update an inventory item (maps frontend fields → backend resource update).
 */
export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, changes }: { id: string; changes: Partial<InventoryItem> }) => {
      const payload = inventoryItemToResourceUpdate(changes);
      return updateResource(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

/**
 * Create a new resource from frontend InventoryItem fields.
 */
export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: Partial<InventoryItem>) => {
      const payload = inventoryItemToResourceUpdate(item);
      // Ensure required fields
      if (!payload.name) throw new Error('Le nom est requis');
      payload.unit = payload.unit || 'unit';
      return createResource(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

/**
 * Adjust quantity (for stock adjustments, waste, etc.)
 */
export function useAdjustQuantity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      quantity,
      movementType,
      notes,
    }: {
      id: string;
      quantity: number;
      movementType: string;
      notes?: string;
    }) => {
      return adjustResourceQuantity(id, quantity, movementType, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}
